<script setup lang="ts">
  import { computed, onMounted, onUnmounted } from 'vue';
  // Components
  import { ColoredButton } from '@/shared/ui';
  // Icons
  import filterIcon from '@/shared/assets/icons/Filter.svg';
  import filterWhite from '@/shared/assets/icons/FilterWhite.svg';
  import orderIcon from '@/shared/assets/icons/Order.svg';
  import orderWhite from '@/shared/assets/icons/OrderWhite.svg';
  import trashIcon from '@/shared/assets/icons/TrashBlackIcon.svg';
  // Composables
  import { useFilterAndSort, type OrderOption, type SortSelectionSource } from '@/shared/lib';

  const props = withDefaults(
    defineProps<{
      orderOptions: readonly OrderOption[];
      isFilterActive?: boolean;
      hasSelectedRecords?: boolean;
      canDelete?: boolean;
      sortAndFilterDisabled?: boolean;
      /** Disables Ordenar por alone; without it, Ordenar por follows `sortAndFilterDisabled`. */
      sortDisabled?: boolean;
      /** The applied sort when it lives outside: this bar remounts on every request of the table. */
      sortSource?: SortSelectionSource | null;
    }>(),
    { canDelete: true, sortAndFilterDisabled: false, sortDisabled: undefined, sortSource: null }
  );

  const emit = defineEmits([
    'update:sort',
    'update:filter',
    'filter-clicked',
    'delete',
  ]);

  const {
    isOrderSelected,
    isOrderDropdownOpen,
    selectedOption,
    dropdownPosition,
    orderButtonRef,
    handleOrder,
    handleFilter,
    sortOptions,
    handleClickOutside,
  } = useFilterAndSort(emit, props.sortSource);

  const handleSortOptions = (sortCamp: string, orderFlow: string) => {
    sortOptions(sortCamp, orderFlow);
  };

  onMounted(() => {
    document.addEventListener('click', handleClickOutside);
  });

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
  });

  const isSortDisabled = computed(() => props.sortDisabled ?? props.sortAndFilterDisabled);

  const handleDelete = () => {
    if (props.canDelete && props.hasSelectedRecords) {
      emit('delete');
    }
  };
</script>

<template>
  <div class="flex items-center gap-3">
    <ColoredButton
      text="Deletar"
      :icon="trashIcon"
      icon-alt="Ícone de exclusão"
      :outlined="true"
      class="!border-gray-03 !text-gray-03"
      :disabled="!props.canDelete"
      @click="handleDelete"
    />

    <div class="relative">
      <div ref="orderButtonRef">
        <ColoredButton
          v-if="isOrderSelected"
          text="Ordenar por"
          :icon="orderWhite"
          icon-alt="Ícone de ordenação"
          :outlined="false"
          :disabled="isSortDisabled"
          @click="handleOrder"
        />
        <ColoredButton
          v-else
          text="Ordenar por"
          :icon="orderIcon"
          icon-alt="Ícone de ordenação"
          :outlined="true"
          :disabled="isSortDisabled"
          @click="handleOrder"
        />
      </div>

      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="transform opacity-0"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition duration-200 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="transform opacity-0"
      >
        <div
          v-if="isOrderDropdownOpen"
          id="order-dropdown"
          class="fixed w-72 p-4 bg-white-01 rounded-2xl shadow-2xl z-50"
          :style="{ top: dropdownPosition.top + 'px', right: dropdownPosition.right + 'px' }"
        >
          <div class="py-1">
            <button
              v-for="option in props.orderOptions"
              :key="option.label"
              class="block w-full text-left px-4 py-2 text-md text-gray-07 font-medium"
              :class="{
                'bg-gray-02 rounded-lg':
                  selectedOption?.sort === option.sort && selectedOption?.order === option.order,
              }"
              @click="handleSortOptions(option.sort, option.order)"
            >
              {{ option.label }}
            </button>
          </div>
        </div>
      </transition>
    </div>

    <ColoredButton
      v-if="isFilterActive"
      text="Filtrar"
      :icon="filterWhite"
      icon-alt="Ícone de filtro"
      :outlined="false"
      :disabled="props.sortAndFilterDisabled"
      @click="handleFilter"
    />
    <ColoredButton
      v-else
      text="Filtrar"
      :icon="filterIcon"
      icon-alt="Ícone de filtro"
      :outlined="true"
      :disabled="props.sortAndFilterDisabled"
      @click="handleFilter"
    />
  </div>
</template>
